import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, tokenStore } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { formatDateTime } from "@/lib/utils";

async function downloadCsv() {
  try {
    const res = await fetch(`${API_BASE}/admin/reports/credit-ledger.csv?limit=10000`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "credit_ledger.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (err) { toast.error(`Download failed: ${err.message}`); }
}

export default function AdminCreditLedgerPage() {
  const [txns, setTxns] = useState(null);
  const [kind, setKind] = useState("");

  useEffect(() => {
    api.get("/admin/reports/credit-ledger?limit=500").then(({ data }) => setTxns(data));
  }, []);

  if (!txns) return <LoadingScreen />;

  const kinds = ["purchase", "freeze", "consume", "refund", "grant", "adjust"];
  const filtered = kind ? txns.filter((t) => t.kind === kind) : txns;

  const total = kinds.reduce((acc, k) => {
    acc[k] = txns.filter((t) => t.kind === k).reduce((s, t) => s + Math.abs(t.delta), 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-credit-ledger">
      <div className="flex justify-end">
        <button
          onClick={downloadCsv}
          data-testid="csv-credit-ledger"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B6936] hover:text-[#09582D] border border-[#538135]/30 hover:border-[#538135] rounded-md px-3 py-1.5 transition-colors"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {kinds.map((k) => (
          <button
            key={k}
            onClick={() => setKind(kind === k ? "" : k)}
            data-testid={`credit-filter-${k}`}
            className={`sqit-card p-4 text-left transition-colors ${kind === k ? "border-[#538135] bg-[#EAF4E3]/40" : ""}`}
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{k}</div>
            <div className="mt-1 font-display text-xl font-bold text-gray-900">{total[k]}</div>
          </button>
        ))}
      </div>

      <div className="sqit-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">When</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kind</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Δ</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Reference</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} className="border-b border-gray-100">
                <td className="px-6 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDateTime(t.created_at)}</td>
                <td className="px-6 py-3">
                  <div className="text-gray-900 text-xs">{t.user_name}</div>
                  <div className="text-[11px] text-gray-500">{t.user_email}</div>
                </td>
                <td className="px-6 py-3 text-xs capitalize">{t.kind}</td>
                <td className={`px-6 py-3 font-semibold ${t.delta >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                  {t.delta > 0 ? "+" : ""}{t.delta}
                </td>
                <td className="px-6 py-3 text-gray-700">{t.balance_after}</td>
                <td className="px-6 py-3 text-xs text-gray-500">
                  {t.meta?.job_code || t.meta?.invoice_no || t.meta?.label || t.meta?.reason || "—"}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-500">No transactions match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
